import { Button, Close } from 'components/Buttons'
import { Modal } from 'components/Modal'
import { useURLParams } from 'hooks/useURLParams'
import { ReadResult } from 'interfaces/queryOptions'
import { Client } from 'models'
import { createManyStaffPermissions } from 'modules/Staff/services/api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import config from 'config'
import { toast } from 'react-toastify'
import { list } from 'modules/Clients/services/read'
import { useNavigate } from 'react-router-dom'
import { Pagination } from 'components/Pagination'
import { LoadingCircle } from 'components/Loading'
import { document } from 'utils'
import { SearchBar } from 'components/Search'

interface Props {
  open: boolean
  setOpen: (open: boolean) => any
  staffId: string
  staffClientIds: string[]
}

export function CreateStaffPermission({
  open,
  setOpen,
  staffId,
  staffClientIds
}: Props) {
  const [clientIds, setClientIds] = useState<number[]>([])

  const navigate = useNavigate()

  const query = useURLParams()
  const queryParams = useMemo(() => {
    const params = {
      search: ''
    }

    const search = query.get('search')

    if (search) params.search = search

    return params
  }, [query])

  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)

  const [search, setSearch] = useState(queryParams.search)
  const [sort] = useState({ account_name: 1 })
  const [total, setTotal] = useState(0)

  const [data, setData] = useState<ReadResult<Client>[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const maxItemsPerPage = config.items_per_list_page

  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)

      const options = {
        limit: maxItemsPerPage,
        search,
        page,
        sort,
        ...(staffClientIds.length > 0 && {
          filters: [{ key: 'id', op: 'nin', value: staffClientIds }]
        })
      }

      try {
        const { count, data } = await list<Client>('/clients', options)
        setData(data)
        setTotal(count)
        setIsLoading(false)
      } catch (err) {
        setIsLoading(false)
        toast.error('Falha ao buscar clientes')
        console.error(err)
      }
    }
    loadData()
  }, [maxItemsPerPage, page, search, sort])

  useEffect(() => {
    const newLastPage = Math.ceil(total / maxItemsPerPage)
    setLastPage(newLastPage)
  }, [maxItemsPerPage, total])

  useEffect(() => {
    if (open) {
      setClientIds([])
    }
  }, [open])

  useEffect(() => {
    setClientIds([])
    setPage(1)
  }, [search])

  const onPageChange = useCallback(
    (page: number) => {
      const newPage = Math.min(Math.max(1, page), lastPage)
      setPage(newPage)
      setClientIds([])
    },
    [lastPage]
  )

  const onSearch = useCallback(
    (term: string) => {
      if (!term || !term.length || term.length < config.min_search_length) {
        term = ''
      }

      if (term === '') {
        query.delete('search')
      } else {
        query.set('search', term)
      }

      navigate(
        {
          pathname: window.location.pathname,
          search: query.toString()
        },
        { replace: true }
      )

      setSearch(term)
    },
    [query, navigate]
  )

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const toggleClientSelection = useCallback((clientId: number) => {
    setClientIds((prev) => {
      if (prev.includes(clientId)) {
        return prev.filter((id) => id !== clientId)
      } else {
        return [...prev, clientId]
      }
    })
  }, [])

  const selectAllClients = useCallback(() => {
    setClientIds((prev) => {
      // Se já tem selecionado todos, limpa. Senão, seleciona todos
      if (prev.length === data.length && data.length > 0) {
        return []
      }
      return data.map((client) => client.id)
    })
  }, [data])

  const onSave = useCallback(async () => {
    if (!staffId || clientIds.length === 0) return

    setIsSaving(true)

    try {
      await createManyStaffPermissions(
        staffId,
        clientIds as unknown as string[]
      )
      toast.success('Permissões adicionadas com sucesso')

      onClose()
      setIsSaving(false)

      navigate(`/staff-members/${staffId}/view/clients`)
      window.location.reload()
    } catch (err: any) {
      setIsSaving(false)
      toast.error(
        err.suggestedMessage ?? 'Não foi possível adicionar as permissões'
      )
    }
  }, [staffId, clientIds, onClose, history])

  return (
    <Modal
      open={open}
      title="Adicionar permissões"
      onClose={onClose}
      width="sm:w-1/2"
    >
      <div className="mt-4 space-y-5">
        <SearchBar value={search} onChange={onSearch} />
        {isLoading ? (
          <LoadingCircle />
        ) : (
          <div className="flex flex-col animate-fade-in-down">
            <div className="px-4 py-3">
              <span className="text-sm font-medium text-gray-700">
                Selecionados: {clientIds.length}
              </span>
            </div>
            <div className="-my-2 overflow-x-auto px-4">
              <div className="py-2 align-middle inline-block min-w-full">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-center text-sm font-light text-gray-500 w-12"
                        >
                          <input
                            type="checkbox"
                            checked={
                              data.length > 0 &&
                              data.every((client) =>
                                clientIds.includes(client.id)
                              )
                            }
                            onChange={selectAllClients}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          CONTA
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          EMPRESA
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((client, index) => (
                        <tr
                          key={client.id}
                          className={
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }
                        >
                          <td className="px-4 py-3 text-center text-sm whitespace-nowrap w-12">
                            <input
                              type="checkbox"
                              checked={clientIds.includes(client.id)}
                              onChange={() => toggleClientSelection(client.id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap max-w-xxs">
                            <div
                              title={client.attributes.account_name}
                              className="text-xs text-left font-semibold text-gray-900 truncate"
                            >
                              {client.attributes.account_name ?? '-'}
                            </div>
                            <div className="text-xs text-left text-gray-500">
                              {client.attributes.email ?? '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left max-w-xxs text-sm whitespace-nowrap">
                            <div
                              title={
                                client.attributes.corporate_data?.corporate_name
                              }
                              className="text-xs text-left font-semibold text-gray-900 truncate"
                            >
                              {client.attributes.corporate_data
                                ?.corporate_name ?? '-'}
                            </div>
                            <div className="text-xs text-left text-gray-500">
                              {document(
                                client.attributes.document,
                                client.attributes.document_type
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mx-4">
              <Pagination
                currentPage={page}
                pageSize={maxItemsPerPage}
                itemsOnPage={data.length}
                totalItems={total}
                onPageChange={onPageChange}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end space-x-4">
        <Close onClick={onClose} />
        <Button disabled={isSaving} type="button" onClick={onSave} />
      </div>
    </Modal>
  )
}
